/* ──────────────────────  PendingOrders.jsx  ────────────────────── */
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, CardHeader, Chip, Divider, Grid,
  Stack, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  RadioGroup, FormControlLabel, Radio, TextField, Paper, Avatar, Badge, IconButton,
  Tabs, Tab, AppBar
} from '@mui/material';
import {
  CheckCircle, LocalDining, Restaurant, Close, ShoppingCart,
  Percent, CurrencyRupee, Timer, Print
} from '@mui/icons-material';
import { styled, createTheme, ThemeProvider } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    primary: { main: '#006600' },
    success: { main: '#28a745' },
    warning: { main: '#fb8c00' },
    info: { main: '#17a2b8' },
  },
  typography: { fontFamily: '"Poppins", sans-serif' },
});

/* ────────────────────── Styled Components ────────────────────── */
const OrderCard = styled(Card)(({ theme }) => ({
  borderRadius: 20,
  boxShadow: '0 6px 20px rgba(0,0,0,0.1)',
  transition: 'all 0.25s ease',
  position: 'relative',
  '&:hover': { transform: 'translateY(-6px)', boxShadow: '0 16px 32px rgba(0,102,0,0.15)' },
}));

const StatusBadge = styled(Chip)(({ theme, status }) => ({
  fontWeight: 700,
  fontSize: '0.75rem',
  height: 28,
  borderRadius: 14,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  backgroundColor: {
    pending: '#fff8e1',
    accepted: '#e8f5e9',
    preparing: '#e3f2fd',
    ready: '#fff3e0',
    paid: '#e8f5e9',
  }[status],
  color: {
    pending: '#ff8f00',
    accepted: '#43a047',
    preparing: '#1976d2',
    ready: '#f57c00',
    paid: '#2e7d32',
  }[status],
}));

const UnitChip = styled(Chip)(({ theme }) => ({
  fontSize: '0.65rem',
  height: 20,
  fontWeight: 600,
  textTransform: 'capitalize',
  backgroundColor: '#e8f5e9',
  color: '#1b5e20',
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.85rem',
  padding: '6px 12px',
  minWidth: 90,
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  transition: 'all 0.2s',
  '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' },
}));

const PrintTopRightButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: 12,
  right: 12,
  backgroundColor: '#006600',
  color: '#fff',
  width: 36,
  height: 36,
  '&:hover': { backgroundColor: '#004d00', transform: 'scale(1.1)' },
}));

export default function PendingOrders() {
  /* ────── STATE ────── */
  const [allOrders, setAllOrders] = useState([]);
  const [profile, setProfile] = useState({ name: '', address: '', phone: '', logo_url: '' });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);               // 0 = active, 1 = paid
  const [paymentOpen, setPaymentOpen] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(null);
  const [discountType, setDiscountType] = useState('amount');
  const [discountValue, setDiscountValue] = useState('');
  const [printOrder, setPrintOrder] = useState(null);

  const navigate = useNavigate();
  const token = localStorage.getItem('admin_token');
  const api = process.env.REACT_APP_API_URL;
  const restaurantName = profile.name || localStorage.getItem('restaurant_name') || 'Your Restaurant';

  /* ────── DERIVED LISTS ────── */
  const { activeOrders, todayPaidOrders } = useMemo(() => {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
    const todayEnd   = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);

    const active = allOrders.filter(o => o.status !== 'paid');
    const paid = allOrders
      .filter(o => o.status === 'paid')
      .filter(o => {
        const dateStr = o.paid_at || o.created_at;
        const orderDate = new Date(dateStr);
        return orderDate >= todayStart && orderDate <= todayEnd;
      });

    return { activeOrders: active, todayPaidOrders: paid };
  }, [allOrders]);

  const ordersToShow = tab === 0 ? activeOrders : todayPaidOrders;

  /* ────── PRINT FUNCTION ────── */
  const handlePrint = (order) => {
    setPrintOrder(order);
    setTimeout(() => {
      const el = document.getElementById('print-slip-block');
      if (!el) return;

      const printWindow = window.open('', '', 'height=600,width=800');
      if (!printWindow) { alert('Please allow pop-ups for printing.'); return; }

      const pageCSS = `
        @page { size: 80mm auto; margin: 0; }
        body { font-family: "Courier New", monospace; font-size: 10pt; line-height: 1.4; padding: 8mm; width: 80mm; }
        table { width: 100%; border-collapse: collapse; }
        .dashed { border-top: 1px dashed #000; margin: 6mm 0; }
        .text-right { text-align: right; }
        .text-center { text-align: center; }
        .bold { font-weight: bold; }
        .note { color: #d32f2f; font-style: italic; font-size: 8pt; }
        .logo { width: 40mm; height: auto; margin-bottom: 4mm; }
      `;

      printWindow.document.write(`
        <html><head><meta charset="UTF-8"><title>Order #${order.id}</title>
        <style>${pageCSS}</style></head>
        <body onload="window.print()" onafterprint="window.close()">
          ${el.innerHTML}
        </body></html>
      `);
      printWindow.document.close();
    }, 100);
  };

  /* ────── LOAD ORDERS + PROFILE ────── */
  useEffect(() => {
    if (!token) { navigate('/admin/login'); return; }

    const loadAll = async () => {
      try {
        const [ordersRes, profileRes] = await Promise.all([
          axios.get(`${api}/admin-orders.php?status=all`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${api}/admin-profile.php`,   { headers: { Authorization: `Bearer ${token}` } })
        ]);

        setAllOrders(ordersRes.data);

        const logoUrl = profileRes.data.logo_url ? `${api}/${profileRes.data.logo_url}` : '';
        setProfile({
          name: profileRes.data.name || '',
          address: profileRes.data.address || '',
          phone: profileRes.data.phone || '',
          logo: logoUrl,
        });
      } catch (err) {
        if (err.response?.status === 401) {
          localStorage.removeItem('admin_token');
          localStorage.removeItem('restaurant_id');
          navigate('/admin/login');
        }
      } finally {
        setLoading(false);
      }
    };

    loadAll();
    const int = setInterval(loadAll, 8000);
    return () => clearInterval(int);
  }, [token, navigate, api]);

  const updateStatus = async (orderId, newStatus) => {
    try {
      await axios.post(`${api}/admin-update-order.php`, { order_id: orderId, status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAllOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: newStatus } : o));
    } catch (e) { console.error(e); }
  };

  const processPayment = async () => {
    const order = allOrders.find(o => o.id === paymentOpen?.orderId);
    if (!order) return;

    const discount = parseFloat(discountValue) || 0;
    const discountAmt = discountType === 'percent' ? (order.total * discount) / 100 : discount;
    const final = Math.max(0, order.total - discountAmt);

    try {
      await axios.post(`${api}/admin-process-payment.php`, {
        order_id: order.id,
        discount_type: discountType,
        discount_value: discount,
        final_amount: final,
      }, { headers: { Authorization: `Bearer ${token}` } });

      setAllOrders(prev => prev.map(o => o.id === order.id ? { ...o, status: 'paid', paid_at: new Date().toISOString() } : o));
      setPaymentOpen(null);
      setDiscountValue('');
      setConfirmOpen({ finalAmount: final });
      setTimeout(() => setConfirmOpen(null), 2500);
    } catch (e) { console.error(e); }
  };

  /* ────── UI HELPERS ────── */
  const getStatusLabel = (s) => ({
    pending: 'Pending',
    accepted: 'Accepted',
    preparing: 'Preparing',
    ready: 'Ready',
    paid: 'Paid',
  }[s]);

  if (loading) {
    return (
      <Box textAlign="center" py={8}>
        <Typography variant="h6" color="text.secondary" fontWeight={600}>
          Loading Orders…
        </Typography>
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ p: { xs: 1.5, md: 2 }, bgcolor: '#f8fafc' }}>
        {/* ────── TAB BAR ────── */}
        <AppBar position="static" color="transparent" elevation={0} sx={{ mb: 2 }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            indicatorColor="primary"
            textColor="primary"
            centered
            sx={{ '& .MuiTab-root': { fontWeight: 600, textTransform: 'none' } }}
          >
            <Tab
              label={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography style={{marginRight:'10px'}}>Active Orders</Typography>
                  <Badge badgeContent={activeOrders.length} color="error" />
                </Stack>
              }
            />
            <Tab
              label={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography style={{marginRight:'10px'}}>Paid Today</Typography>
                  <Badge badgeContent={todayPaidOrders.length} color="success" />
                </Stack>
              }
            />
          </Tabs>
        </AppBar>

        {/* ────── NO ORDERS MESSAGE ────── */}
        {ordersToShow.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: 6,
              textAlign: 'center',
              borderRadius: 3,
              bgcolor: 'linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%)',
              border: '2px dashed #81c784',
            }}
          >
            <Typography variant="h6" color="#43a047" fontWeight={700} gutterBottom>
              {tab === 0 ? 'All Caught Up!' : 'No Payments Today'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {tab === 0 ? 'No active orders right now.' : 'No orders have been paid yet today.'}
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={2}>
            {ordersToShow.map(order => {
              const canPay = order.status === 'ready';
              const isPaid = order.status === 'paid';

              return (
                <Grid item xs={12} sm={6} lg={3} key={order.id}>
                  <OrderCard>
                    {/* PRINT BUTTON (only for non-paid) */}
                 
                      <PrintTopRightButton onClick={() => handlePrint(order)}>
                        <Print />
                      </PrintTopRightButton>
                  

                    <CardHeader
                      avatar={
                        <Avatar
                          sx={{
                            bgcolor: '#e8f5e9',
                            color: '#006600',
                            width: 40,
                            height: 40,
                            fontSize: '0.9rem',
                            fontWeight: 700,
                          }}
                        >
                          #{order.id}
                        </Avatar>
                      }
                      title={
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography variant="subtitle1" fontWeight={700}>
                            T{order.table_number}
                          </Typography>
                          <StatusBadge label={getStatusLabel(order.status)} status={order.status} />
                        </Stack>
                      }
                      subheader={
                        <Stack direction="row" alignItems="center" spacing={0.5} mt={0.5}>
                          <Timer fontSize="small" />
                          <Typography variant="caption" color="text.secondary" fontWeight={500}>
                            {order.time_ago}
                          </Typography>
                        </Stack>
                      }
                      sx={{ pb: 1.5, pr: isPaid ? 2 : 6 }}
                    />
                    <CardContent sx={{ pt: 1, pb: 2, px: 2 }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                        sx={{ fontWeight: 500, fontSize: '0.85rem' }}
                      >
                        {order.customer_name || 'Guest'}
                      </Typography>

                      {/* ITEMS LIST */}
                      <Box
                        sx={{
                          bgcolor: '#fff',
                          border: '1px solid #e0e0e0',
                          borderRadius: 3,
                          p: 2,
                          my: 1.5,
                          maxHeight: 180,
                          overflowY: 'auto',
                          boxShadow: 'inset 0 1px 4px rgba(0,0,0,0.02)',
                        }}
                      >
                        {order.items.map((it, i) => {
                          const lineTotal = (it.unit_price * it.quantity).toFixed(2);
                          return (
                            <Stack
                              key={i}
                              direction="row"
                              justifyContent="space-between"
                              alignItems="flex-start"
                              py={1}
                              borderBottom={i < order.items.length - 1 ? '1px dashed #eee' : 'none'}
                            >
                              <Box sx={{ flex: 1 }}>
                                <Stack direction="row" alignItems="center" spacing={0.8}>
                                  <Typography
                                    variant="body2"
                                    fontWeight={600}
                                    sx={{ fontSize: '0.85rem', lineHeight: 1.3 }}
                                  >
                                    {it.quantity}× {it.name}
                                  </Typography>
                                  {it.unit && <UnitChip label={it.unit} />}
                                </Stack>
                                {it.notes && (
                                  <Typography
                                    variant="caption"
                                    color="error"
                                    sx={{ fontStyle: 'italic', fontSize: '0.75rem', mt: 0.3, display: 'block' }}
                                  >
                                    {it.notes}
                                  </Typography>
                                )}
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                  ₹{it.unit_price} ea
                                </Typography>
                              </Box>
                              <Typography
                                variant="body2"
                                fontWeight={700}
                                color="#006600"
                                sx={{ minWidth: 60, textAlign: 'right', fontSize: '0.85rem' }}
                              >
                                ₹{lineTotal}
                              </Typography>
                            </Stack>
                          );
                        })}
                      </Box>

                      <Divider sx={{ my: 1.5 }} />

                      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                        <Typography variant="h6" fontWeight={800} color="#033303" sx={{ fontSize: '1.1rem' }}>
                          ₹{order.total}
                        </Typography>

                        {/* ACTION BUTTONS (only for active orders) */}
                        {!isPaid && (
                          <Stack direction="row" spacing={0.8} flexWrap="wrap" justifyContent="flex-end">
                            {order.status === 'pending' && (
                              <ActionButton
                                size="small"
                                color="success"
                                startIcon={<CheckCircle />}
                                onClick={() => updateStatus(order.id, 'accepted')}
                              >
                                Accept
                              </ActionButton>
                            )}
                            {order.status === 'accepted' && (
                              <ActionButton
                                size="small"
                                sx={{ bgcolor: '#fb8c00', '&:hover': { bgcolor: '#f57c00' } }}
                                startIcon={<LocalDining />}
                                onClick={() => updateStatus(order.id, 'preparing')}
                              >
                                Prep
                              </ActionButton>
                            )}
                            {order.status === 'preparing' && (
                              <ActionButton
                                size="small"
                                color="primary"
                                startIcon={<Restaurant />}
                                onClick={() => updateStatus(order.id, 'ready')}
                              >
                                Ready
                              </ActionButton>
                            )}
                            {canPay && (
                              <ActionButton
                                size="small"
                                color="success"
                                startIcon={<CurrencyRupee />}
                                onClick={() => setPaymentOpen({ orderId: order.id, total: order.total })}
                              >
                                Pay
                              </ActionButton>
                            )}
                          </Stack>
                        )}
                        {isPaid && (
                          <Chip
                            label="Paid"
                            color="success"
                            size="small"
                            sx={{ fontWeight: 700, textTransform: 'uppercase' }}
                          />
                        )}
                      </Stack>
                    </CardContent>
                  </OrderCard>
                </Grid>
              );
            })}
          </Grid>
        )}

        {/* ────── HIDDEN PRINT BLOCK (with logo, name, address, phone) ────── */}
        <div style={{ display: 'none' }}>
          {printOrder && (
            <div id="print-slip-block">
              {/* LOGO */}
              {profile.logo_url && (
                <div className="text-center">
                  <img src={profile.logo_url} alt="Logo" className="logo" />
                </div>
              )}

              {/* RESTAURANT NAME */}
              <div className="text-center bold" style={{ fontSize: '14pt', marginBottom: '2mm' }}>
                {profile.name}
              </div>

              {/* ADDRESS & PHONE */}
              {profile.address && (
                <div className="text-center" style={{ fontSize: '9pt', marginBottom: '1mm' }}>
                  {profile.address}
                </div>
              )}
              {profile.phone && (
                <div className="text-center" style={{ fontSize: '9pt', marginBottom: '4mm' }}>
                  {profile.phone}
                </div>
              )}

              <div className="text-center" style={{ fontSize: '10pt', marginBottom: '6mm' }}>
                Order Slip
              </div>
              <div className="dashed"></div>

              <table>
                <tbody>
                  <tr><td><strong>Order ID:</strong></td><td className="text-right">#{printOrder.id}</td></tr>
                  <tr><td><strong>Table:</strong></td><td className="text-right">T{printOrder.table_number}</td></tr>
                  <tr>
                    <td><strong>Time:</strong></td>
                    <td className="text-right">
                      {new Date(printOrder.created_at).toLocaleString('en-IN', {
                        day: '2-digit', month: 'short', year: 'numeric',
                        hour: '2-digit', minute: '2-digit',
                      })}
                    </td>
                  </tr>
                  {printOrder.customer_name && (
                    <tr><td><strong>Customer:</strong></td><td className="text-right">{printOrder.customer_name}</td></tr>
                  )}
                </tbody>
              </table>

              <div className="dashed"></div>

              {printOrder.items.map((it, i) => (
                <div key={i} style={{ marginBottom: '4mm' }}>
                  <div className="bold">
                    {it.quantity} × {it.name} {it.unit && `(${it.unit})`}
                  </div>
                  {it.notes && <div className="note">Note: {it.notes}</div>}
                  <div className="text-right bold">
                    ₹{(it.unit_price * it.quantity).toFixed(2)}
                  </div>
                </div>
              ))}

              <div className="dashed"></div>

              <table>
                <tbody>
                  <tr>
                    <td className="bold" style={{ fontSize: '14pt' }}>Total:</td>
                    <td className="text-right bold" style={{ fontSize: '14pt' }}>
                      ₹{printOrder.total}
                    </td>
                  </tr>
                </tbody>
              </table>

              <div className="dashed"></div>
              <div className="text-center" style={{ fontSize: '9pt' }}>
                Thank You!<br />Visit Again
              </div>
            </div>
          )}
        </div>

        {/* ────── PAYMENT DIALOG ────── */}
        <Dialog open={!!paymentOpen} onClose={() => { setPaymentOpen(null); setDiscountValue(''); }} maxWidth="sm" fullWidth>
          <DialogTitle sx={{
            bgcolor: 'linear-gradient(to right, #339933, #006600)',
            color: '#fff',
            py: 3,
            textAlign: 'center',
            fontWeight: 700,
            fontSize: '1.5rem'
          }}>
            Process Payment
            <IconButton sx={{ position: 'absolute', right: 12, top: 12, color: '#fff' }}
              onClick={() => { setPaymentOpen(null); setDiscountValue(''); }}>
              <Close />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ pt: 4, pb: 3 }}>
            {paymentOpen && (
              <Stack spacing={3}>
                <Typography variant="h6" textAlign="center" fontWeight={600}>
                  Order #{paymentOpen.orderId} • Total: <strong>₹{paymentOpen.total}</strong>
                </Typography>

                <RadioGroup value={discountType} onChange={e => setDiscountType(e.target.value)} row sx={{ justifyContent: 'center' }}>
                  <FormControlLabel value="amount" control={<Radio color="success" />} label="Discount (₹)" />
                  <FormControlLabel value="percent" control={<Radio color="warning" />} label="Discount (%)" />
                </RadioGroup>

                <TextField
                  fullWidth
                  label={discountType === 'amount' ? 'Enter Discount Amount' : 'Enter Discount Percentage'}
                  type="number"
                  value={discountValue}
                  onChange={e => setDiscountValue(e.target.value)}
                  InputProps={{
                    startAdornment: discountType === 'percent' ? <Percent sx={{ color: 'text.secondary', mr: 1 }} /> : <CurrencyRupee sx={{ color: 'text.secondary', mr: 1 }} />,
                  }}
                  inputProps={{ min: 0, step: discountType === 'percent' ? '0.1' : '1' }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                />

                <Paper elevation={3} sx={{ p: 3, bgcolor: '#e8f5e9', borderRadius: 3, textAlign: 'center' }}>
                  <Typography variant="h5" fontWeight={800} color="#006600">
                    Final Amount: ₹{(() => {
                      const d = parseFloat(discountValue) || 0;
                      const amt = discountType === 'percent' ? (paymentOpen.total * d) / 100 : d;
                      return Math.max(0, paymentOpen.total - amt).toFixed(2);
                    })()}
                  </Typography>
                </Paper>
              </Stack>
            )}
          </DialogContent>

          <DialogActions sx={{ p: 3, bgcolor: '#f8fafc', justifyContent: 'space-between' }}>
            <Button onClick={() => { setPaymentOpen(null); setDiscountValue(''); }} sx={{ borderRadius: 12, fontWeight: 600, px: 4 }}>
              Cancel
            </Button>
            <ActionButton variant="contained" color="success" size="large" onClick={processPayment}
              sx={{ px: 5, py: 1.5, fontSize: '1rem' }}>
              Confirm Payment
            </ActionButton>
          </DialogActions>
        </Dialog>

        {/* ────── SUCCESS DIALOG ────── */}
        <Dialog open={!!confirmOpen} onClose={() => setConfirmOpen(null)}
          PaperProps={{
            sx: {
              borderRadius: 4,
              bgcolor: '#e8f5e9',
              boxShadow: '0 12px 40px rgba(0,102,0,0.2)',
              textAlign: 'center',
              p: 4,
              maxWidth: 400,
            },
          }}>
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <Box sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              bgcolor: '#43a047',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              animation: 'pop 0.6s ease-out',
              '@keyframes pop': { '0%': { transform: 'scale(0)' }, '80%': { transform: 'scale(1.1)' }, '100%': { transform: 'scale(1)' } },
            }}>
              <CheckCircle sx={{ fontSize: 48, color: '#fff' }} />
            </Box>
          </Box>

          <Typography variant="h5" sx={{ mt: 3, fontWeight: 700, color: '#1b5e20' }}>
            Payment Successful!
          </Typography>
          <Typography variant="h6" sx={{ mt: 1, color: '#2e7d32', fontWeight: 600 }}>
            ₹{confirmOpen?.finalAmount.toFixed(2)}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Order has been completed.
          </Typography>
          <Button onClick={() => setConfirmOpen(null)} sx={{
            mt: 3,
            bgcolor: '#2e7d32',
            color: '#fff',
            borderRadius: 3,
            px: 4,
            fontWeight: 600,
            textTransform: 'none',
            '&:hover': { bgcolor: '#1b5e20' },
          }}>
            Got it
          </Button>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
}